import { Button, CircularProgress } from '@civilgrid/ui/components';
import { useEffect, useMemo, useState } from 'react';
import { getAdminTileLayers, updateDisabledTileLayers } from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { AdminTileLayer } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { MajorHeading } from '../common/Heading';
import { disabledKeysOf, filterLayers, isDisabledDirty, safeHttpHref, toggleLayerEnabled } from './tileLayerToggles';

export default function OrgLayerToggles() {
  const [layers, setLayers] = useState<AdminTileLayer[]>([]);
  // Snapshot of disabled keys as last loaded/saved, to detect unsaved edits.
  const [initialDisabled, setInitialDisabled] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    getAdminTileLayers()
      .then((rows) => {
        if (!active) return;
        setLayers(rows);
        setInitialDisabled(disabledKeysOf(rows));
        setLoadError(false);
      })
      .catch((e) => {
        console.error('Error loading tile layers:', e);
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const visible = useMemo(() => filterLayers(layers, search), [layers, search]);
  const currentDisabled = useMemo(() => disabledKeysOf(layers), [layers]);
  const isDirty = useMemo(() => isDisabledDirty(currentDisabled, initialDisabled), [currentDisabled, initialDisabled]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const saved = await updateDisabledTileLayers(currentDisabled);
      capturePosthogEvent(POSTHOG_EVENTS.tileLayersDisabledUpdated, {
        disabled_count: saved.length,
      });
      setInitialDisabled(saved);
      // Reconcile local state so toggles reflect exactly what persisted.
      setLayers((prev) => prev.map((l) => ({ ...l, enabled: !saved.includes(l.key) })));
      Notify.info({ message: 'Layer availability updated' });
    } catch (e) {
      console.error('Error updating disabled layers:', e);
      Notify.error({ message: 'Error updating layers. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 mb-10">
      <MajorHeading>Layer Availability</MajorHeading>
      <p className="text-sm text-grey-60">
        Layers are enabled by default. Disabling a layer removes it from the delivered catalog for all organizations.
      </p>

      {isLoading && <CircularProgress indeterminate />}

      {loadError && !isLoading && (
        <span className="text-sm text-red-60" data-testid="layer-toggles-load-error">
          Couldn't load layers. Please reload the page.
        </span>
      )}

      {!isLoading && !loadError && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <span className="font-semibold">Search:</span>
            <input
              data-testid="layer-toggles-search"
              className="border rounded px-2 py-1 text-sm"
              value={search}
              placeholder="Filter by label, key, category, special dataset, or Esri URL…"
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="text-sm text-grey-60">
              {currentDisabled.length} disabled / {layers.length} total
            </span>
          </div>

          <table className="w-full text-sm" data-testid="layer-toggles-table">
            <thead>
              <tr className="text-left border-b">
                <th className="py-1 pr-4">Enabled</th>
                <th className="py-1 pr-4">Label</th>
                <th className="py-1 pr-4">Key</th>
                <th className="py-1 pr-4">Category</th>
                <th className="py-1 pr-4">Special Dataset</th>
                <th className="py-1 pr-4">Esri URL</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-2 text-grey-60">
                    No layers match this search.
                  </td>
                </tr>
              )}
              {visible.map((layer) => (
                <tr key={layer.key} className="border-b">
                  <td className="py-1 pr-4">
                    <input
                      type="checkbox"
                      checked={layer.enabled}
                      disabled={isSaving}
                      aria-label={`Toggle ${layer.label}`}
                      data-testid={`layer-toggle-${layer.key}`}
                      onChange={() => setLayers((prev) => toggleLayerEnabled(prev, layer.key))}
                    />
                  </td>
                  <td className="py-1 pr-4">{layer.label}</td>
                  <td className="py-1 pr-4 text-grey-60">{layer.key}</td>
                  <td className="py-1 pr-4 text-grey-60">{layer.category}</td>
                  <td className="py-1 pr-4 text-grey-60" data-testid={`layer-special-dataset-${layer.key}`}>
                    {layer.specialDatasetSlug ?? '—'}
                  </td>
                  <td className="py-1 pr-4 text-grey-60 max-w-xs">
                    {(() => {
                      const safeHref = safeHttpHref(layer.sourceUrl);
                      return safeHref ? (
                        <a
                          href={safeHref}
                          target="_blank"
                          rel="noreferrer"
                          title={layer.sourceUrl}
                          className="block truncate text-blue-50 underline hover:text-blue-60"
                          data-testid={`layer-esri-url-${layer.key}`}
                        >
                          {layer.sourceUrl}
                        </a>
                      ) : (
                        <span
                          title={layer.sourceUrl}
                          className="block truncate"
                          data-testid={`layer-esri-url-${layer.key}`}
                        >
                          {layer.sourceUrl}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center space-x-2">
            <Button
              type="button"
              data-testid="layer-toggles-save"
              loading={isSaving}
              disabled={isSaving || !isDirty}
              onClick={handleSave}
            >
              Save Layer Availability
            </Button>
            {isDirty && (
              <span className="text-sm text-grey-60" data-testid="layer-toggles-unsaved">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
